<template>
  <div class="algolia-range-slider">
    <div class="algolia-range-slider__inputs">
      <div class="algolia-range-slider__inputs-wrapper">
        <span
          class="algolia-range-slider__inputs-currency text-label-xs"
          v-text="$variable('store.currencySymbol')"
        />
        <input
          :value="setMin"
          :aria-label="$string('collection.price_min')"
          class="algolia-range-slider__inputs-input text-label-xs"
          min="0"
          :max="setMax"
          type="text"
          @blur="handleTextInput($event, 'min-text')"
        >
      </div>

      <div class="algolia-range-slider__inputs-divider" />

      <div class="algolia-range-slider__inputs-wrapper">
        <span
          class="algolia-range-slider__inputs-currency text-label-xs"
          v-text="$variable('store.currencySymbol')"
        />
        <input
          :value="setMax"
          :aria-label="$string('collection.price_max')"
          class="algolia-range-slider__inputs-input text-label-xs"
          :min="setMin"
          :max="maxCalculated"
          type="text"
          @blur="handleTextInput($event, 'max-text')"
        >
      </div>
    </div>

    <!-- Range slider -->
    <div
      class="algolia-range-slider__wrap"
      role="group"
      aria-labelledby="multi-lbl"
      :style="`--range-thumb-left: ${setMin}; --range-thumb-right: ${setMax}; --range-min: 0; --range-max: ${maxCalculated}`"
    >
      <label
        class="visually-hidden"
        for="range-slider-left"
        v-text="$string('collection.price_min')"
      />
      <input
        :aria-label="$string('collection.price_min_slider')"
        name="range-slider-left"
        data-range-side="left"
        js-algolia-range-slider="input"
        type="range"
        min="0"
        :value="setMin"
        :max="maxCalculated"
        step="1"
        @input="debouncedHandleRangeInput"
        @touchend="debouncedHandleRangeTouch"
      >

      <label
        class="visually-hidden"
        for="range-slider-right"
        v-text="$string('collection.price_max')"
      />
      <input
        :aria-label="$string('collection.price_max_slider')"
        name="range-slider-right"
        data-range-side="right"
        js-algolia-range-slider="input"
        type="range"
        min="0"
        :value="setMax"
        :max="maxCalculated"
        step="1"
        @input="debouncedHandleRangeInput"
        @touchend="debouncedHandleRangeTouch"
      >
    </div>

    <!-- Range indicators -->
    <div class="algolia-range-slider__indicators">
      <div class="algolia-range-slider__indicators-notch">
        <div class="algolia-range-slider__indicators-line" />
        <p
          class="algolia-range-slider__indicators-value text-body-2xs-desktop text-label-xs"
          v-text="0"
        />
      </div>
      <div
        v-for="(notch, index) in 5"
        :key="index"
        class="algolia-range-slider__indicators-notch"
      >
        <div class="algolia-range-slider__indicators-line" />
        <p
          class="algolia-range-slider__indicators-value text-body-2xs-desktop text-label-xs"
          v-text="notchValue(index)"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Algolia range slider (algolia-range-slider)
 * -----------------------------------------------------------------------------
 * Slider component for displaying range based algolia filtering properties.
 *
 * @param {Object} facet - Facet object from Algolia.
 * @param {Number} max - Max price.
 * @param {Number} min - Min price.
 *
 */
import { mapState } from 'vuex'
import { debounce } from '~/helpers/general'

export default {
  name: 'AlgoliaRangeSlider',

  props: {
    facet: {
      type: Object,
      default: () => {},
    },
    max: {
      type: Number,
      default: 100,
    },
    min: {
      type: Number,
      default: 0,
    },
    value: {
      type: Array,
      default: () => [0, 100],
    },
  },

  emits: ['algolia-range-updated'],

  data() {
    return {
      setMin: 0,
      setMax: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Calculates an appropriate buffer above the max value.
     * @returns {Number}
     */
    maxCalculated() {
     if (this.max % 4 === 0) {
      return this.max + (this.max / 4)
     }

     let calculated = this.max

     while(calculated % 4 !== 0) {
      calculated++
     }
     return calculated + (calculated / 4)
    },

    /**
     * Calculates where the min handle should start.
     * @returns {Number}
     */
    minValue() {
      return this.maxCalculated / 5
    },
  },

  watch: {

    /**
     * When max is updated then set setMax.
     */
    value() {
      this.setMin = this.value[0]
      this.setMax = this.value[1]
    },
  },

  mounted() {
    this.debouncedHandleRangeInput = debounce(() => this.handleRangeInput(), this.settings.throttle)
    this.debouncedHandleRangeTouch = debounce(() => this.handleRangeTouch(), this.settings.throttle)

    this.setMin = this.value[0]
    this.setMax = this.value[1]
    document.querySelectorAll('input[type="range"]').forEach((input) => {
      input.addEventListener('mousedown',  () => window.getSelection().removeAllRanges())
    })

    this.addRangeEventListeners()
  },

  methods: {

    /**
     * Receives the text inputs and processes them.
     * @param {Object} event - Input event.
     * @param {String} type - Type of input (max-text/min-text).
     */
    handleTextInput(event, type){
      const value = event.target.value

      if (type === 'max-text'){
        this.validateSetMax(value)
      } else if(type === 'min-text'){
        this.validateSetMin(value)
      }

      this.updateRangeData()
    },

    /**
     * Process the input from the range slider on desktop.
     */
    handleRangeInput(){
      if (this.isMobile){
        return
      }

      this.validateSetMax()
      this.validateSetMin()
      this.updateRangeData()
    },

    /**
     * On mobile, handle the touch end input.
     */
    handleRangeTouch(){
      if (!this.isMobile){
        return
      }

      this.validateSetMax()
      this.validateSetMin()
      this.updateRangeData()
    },

    /**
     * Validate the input in the min value text input.
     * @param {Number} value - Input number value.
     */
    validateSetMin(value){
      const minValue = value ?? this.setMin

      if (minValue >= this.setMax) {
        this.setMin = this.setMax
        return
      }

      if (minValue <= 0) {
        this.setMin = 0
        return
      }

      if (minValue > this.maxCalculated) {
        this.setMin = this.maxCalculated
        return
      }

      this.setMin = minValue
    },

    /**
     * Validate the input in the max value text input.
     * @param {Number} value - Input number value.
     */
    validateSetMax(value){
      const maxValue = value ?? this.setMax

      if (maxValue <= this.setMin) {
        this.setMax = this.setMin
        return
      }

      if (maxValue <= 0) {
        this.setMax = 0
        return
      }

      if (maxValue > this.maxCalculated) {
        this.setMax = this.maxCalculated
        return
      }

      this.setMax = maxValue
    },

    /**
     * Updates the range data processed by related components.
     */
    updateRangeData() {
      let max = this.setMax
      let min = this.setMin

      if(this.setMax >= this.max) {
        max = this.max
      }

      if(this.setMin <= this.min) {
        min = this.min
      }

      this.$emit('algolia-range-updated', { min, max })
    },

    /**
     * Calculates values for slider notches relative to max price.
     * @param {Number} index - Index in the for loop.
     * @returns {Number}
     */
    notchValue(index) {
      return (this.maxCalculated / 5) * (index + 1)
    },

    /**
     * Adds event listeners to the range slider inputs.
     */
    addRangeEventListeners() {
      const sliders = [...document.querySelectorAll('[js-algolia-range-slider="input"]')]

      sliders.forEach(slider => {

        slider.addEventListener('input', event => {

          const target = event.target

          if (target.dataset.rangeSide === 'left') {
            if (Number(target.value) >= this.setMax) {
              this.setMax = Number(target.value)
            }
            this.setMin = Number(target.value)
          }

          if (target.dataset.rangeSide === 'right') {
            if (Number(target.value) <= this.setMin) {
              this.setMin = Number(target.value)
            }
            this.setMax = Number(target.value)
          }

          target.parentNode.style.setProperty(
            `--range-thumb-${target.dataset.rangeSide}`,
            + target.value,
          )
        }, false)
      })
    },
  },
}
</script>

<style lang="scss">
@import './algolia-range-slider';
</style>
